import type { ReactElement } from 'react';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import {
  NeonBreadcrumbs,
  NeonButton,
  NeonButtonFooter,
  NeonCardStandard,
  NeonErrorPageContent,
  NeonModalDialog,
  NeonTabs,
} from '@ps-refarch-ux/neon';
import type { Tab } from '@ps-refarch-ux/neon';
import { DATA_SLOT, NeonButtonTypes } from '@/constants/neon-constants';
import {
  getHomeUrl,
  isCoursePlannerPlanApprovalOptionsEnabled,
  isSchoolSelectionFeatureEnabled,
  isTermSettingsFeatureEnabled,
} from '@/utils/cp-context';
import { usePageFooter } from '@/context';
import {
  CONFIGURATIONS_ID,
  CONFIGURATIONS_TEXT,
  CONFIGURATIONS_TAB_IDS,
  APPROVAL_REQUIREMENT_VALUES,
  STUDENT_SUBMISSION_VALUES,
  STAFF_APPROVAL_VALUES,
  CONFIGURATIONS_BUTTON_CANCEL,
  CONFIGURATIONS_BUTTON_SAVE,
  LEAVE_MODAL_TITLE,
  LEAVE_MODAL_MESSAGE,
  LEAVE_MODAL_LEAVE,
  LEAVE_MODAL_CONTINUE,
  CONFIGURATIONS_SAVE_TOAST_HEADING,
  CONFIGURATIONS_SAVE_ERROR_HEADING,
  TERM_NAMES_REQUIRED_TOAST_HEADING,
  areStudentExperienceTermsValid,
  areAdditionalResourcesLinksValid,
  areSchoolSelectionGradesValid,
  isAdditionalResourcesContentValid,
  type ApprovalsFormState,
  CREDIT_RECOVERY_VALUES,
  CONFIGURE_LABELS_CLUSTER_DEFAULT,
  CONFIGURE_LABELS_PATHWAY_DEFAULT,
  SCHOOL_SELECTION_VALUES,
  type StudentExperienceFormState,
  type AdditionalResourcesLink,
  ADDITIONAL_RESOURCES_DOC_NAME_PLACEHOLDER,
} from '@/constants/configurations-constants';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import {
  useGetCoursePlannerConfig,
  usePutCoursePlannerConfig,
  usePutSchoolClusterGuide,
  useUploadSchoolClusterGuideDocument,
  type GradeSetting,
  type GradeDeficiency,
} from '@/repositories';
import {
  pageWrapperStyle,
  pageHeaderStyle,
  contentCardStyle,
  titleStyle,
  paragraphStyle,
  tabPanelStyle,
  configStatusContentStyle,
  configurationsActionBarStyle,
  modalBodyTextStyle,
} from './configurations.styles';
import { ApprovalsTab } from './approvals-tab';
import { AcademicRulesTab } from './academic-rules-tab';
import { StudentExperienceTab } from './student-experience-tab';

function arraysEqual<T>(a: T[], b: T[], stringify?: (t: T) => string): boolean {
  if (a.length !== b.length) {
    return false;
  }
  if (stringify) {
    return a.every((x, i) => stringify(x) === stringify(b[i]));
  }
  return JSON.stringify(a) === JSON.stringify(b);
}

const VALID_APPROVAL_REQUIREMENTS = new Set<string>([
  APPROVAL_REQUIREMENT_VALUES.SchoolApproval,
  APPROVAL_REQUIREMENT_VALUES.SchoolAndGuardian,
  APPROVAL_REQUIREMENT_VALUES.NoApproval,
]);

const VALID_STUDENT_SUBMISSIONS = new Set<string>([
  STUDENT_SUBMISSION_VALUES.WithAlerts,
  STUDENT_SUBMISSION_VALUES.NextYearNoAlerts,
  STUDENT_SUBMISSION_VALUES.NoAlerts,
]);

const VALID_STAFF_APPROVALS = new Set<string>([
  STAFF_APPROVAL_VALUES.ApproveAlways,
  STAFF_APPROVAL_VALUES.ApproveAfterSubmission,
]);

const LEGACY_CONFIGURE_LABELS_CLUSTER_DEFAULT = 'Cluster';
const LEGACY_CONFIGURE_LABELS_PATHWAY_DEFAULT = 'Pathway';

function mapLegacyLabelToCurrentDefault(
  configuredLabel: string,
  legacyDefault: string,
  currentDefault: string
): string {
  return configuredLabel.trim().toLowerCase() === legacyDefault.toLowerCase()
    ? currentDefault
    : configuredLabel;
}

const initialApprovalsFormState: ApprovalsFormState = {
  approvalRequirement: APPROVAL_REQUIREMENT_VALUES.SchoolApproval,
  studentSubmission: STUDENT_SUBMISSION_VALUES.WithAlerts,
  staffApproval: STAFF_APPROVAL_VALUES.ApproveAfterSubmission,
};

const initialStudentExperienceFormState: StudentExperienceFormState = {
  clusterLabel: CONFIGURE_LABELS_CLUSTER_DEFAULT,
  pathwayLabel: CONFIGURE_LABELS_PATHWAY_DEFAULT,
  enableTermSetting: false,
  terms: [],
  schoolSelection: SCHOOL_SELECTION_VALUES.AllStudents,
  schoolSelectionGrades: [],
  showToStudents: false,
  links: [],
  documentName: '',
  documentFileName: '',
};

/**
 * Show Student Submission Rules when `feature.coursePlanner.planApprovalOptions` is true in the host’s GET /features
 * payload (`window.CP_COURSE_PLANNER_FEATURES`), and approval rules apply. Course Planner does not call /features.
 */
function shouldShowStudentSubmissionRules(
  approvalRequirement: string | undefined,
  planApprovalOptionsEnabled: boolean
): boolean {
  if (!planApprovalOptionsEnabled || !approvalRequirement) {
    return false;
  }
  return (
    approvalRequirement === APPROVAL_REQUIREMENT_VALUES.SchoolApproval ||
    approvalRequirement === APPROVAL_REQUIREMENT_VALUES.SchoolAndGuardian
  );
}

function isApprovalsFormDirty(current: ApprovalsFormState, saved: ApprovalsFormState): boolean {
  return (
    current.approvalRequirement !== saved.approvalRequirement ||
    current.studentSubmission !== saved.studentSubmission ||
    current.staffApproval !== saved.staffApproval
  );
}

export function Configurations(): ReactElement {
  const navigate = useNavigate();
  const { showToast } = useToastNotifications();
  const {
    data: config,
    isLoading: isConfigLoading,
    error: configError,
  } = useGetCoursePlannerConfig();
  const planApprovalOptionsEnabled = isCoursePlannerPlanApprovalOptionsEnabled();
  const termSettingsEnabled = isTermSettingsFeatureEnabled();
  const schoolSelectionEnabled = isSchoolSelectionFeatureEnabled();
  const putConfig = usePutCoursePlannerConfig();
  // Keep stable refs to mutateAsync so handleSave doesn't need these as deps.
  const putConfigMutateRef = useRef(putConfig.mutateAsync);
  putConfigMutateRef.current = putConfig.mutateAsync;
  const putSchoolClusterGuide = usePutSchoolClusterGuide();
  const putSchoolClusterGuideMutateRef = useRef(putSchoolClusterGuide.mutateAsync);
  putSchoolClusterGuideMutateRef.current = putSchoolClusterGuide.mutateAsync;
  const uploadDocument = useUploadSchoolClusterGuideDocument();
  const uploadDocumentMutateRef = useRef(uploadDocument.mutateAsync);
  uploadDocumentMutateRef.current = uploadDocument.mutateAsync;
  const [currentTab, setCurrentTab] = useState<string>(CONFIGURATIONS_TAB_IDS.Approvals);

  const [approvalsFormState, setApprovalsFormState] =
    useState<ApprovalsFormState>(initialApprovalsFormState);
  const [approvalsSavedState, setApprovalsSavedState] =
    useState<ApprovalsFormState>(initialApprovalsFormState);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const [creditRecovery, setCreditRecovery] = useState<string>(CREDIT_RECOVERY_VALUES.Prompt);
  const [savedCreditRecovery, setSavedCreditRecovery] = useState<string>(
    CREDIT_RECOVERY_VALUES.Prompt
  );
  const [gradeSettings, setGradeSettings] = useState<GradeSetting[]>([]);
  const [savedGradeSettings, setSavedGradeSettings] = useState<GradeSetting[]>([]);
  const [gradeDeficiency, setGradeDeficiency] = useState<GradeDeficiency[]>([]);
  const [savedGradeDeficiency, setSavedGradeDeficiency] = useState<GradeDeficiency[]>([]);

  const [studentExperienceFormState, setStudentExperienceFormState] =
    useState<StudentExperienceFormState>(initialStudentExperienceFormState);
  const [studentExperienceSavedState, setStudentExperienceSavedState] =
    useState<StudentExperienceFormState>(initialStudentExperienceFormState);
  const [highlightEmptyTermNames, setHighlightEmptyTermNames] = useState(false);
  const [highlightIncompleteLinks, setHighlightIncompleteLinks] = useState(false);
  const [highlightSchoolSelectionGrades, setHighlightSchoolSelectionGrades] = useState(false);
  const [highlightMissingAdditionalContent, setHighlightMissingAdditionalContent] = useState(false);

  useEffect(() => {
    if (highlightEmptyTermNames && areStudentExperienceTermsValid(studentExperienceFormState)) {
      setHighlightEmptyTermNames(false);
    }
  }, [studentExperienceFormState, highlightEmptyTermNames]);

  useEffect(() => {
    if (
      highlightIncompleteLinks &&
      areAdditionalResourcesLinksValid(studentExperienceFormState.links)
    ) {
      setHighlightIncompleteLinks(false);
    }
  }, [studentExperienceFormState.links, highlightIncompleteLinks]);

  useEffect(() => {
    if (
      highlightSchoolSelectionGrades &&
      areSchoolSelectionGradesValid(studentExperienceFormState)
    ) {
      setHighlightSchoolSelectionGrades(false);
    }
  }, [studentExperienceFormState, highlightSchoolSelectionGrades]);

  useEffect(() => {
    if (
      highlightMissingAdditionalContent &&
      isAdditionalResourcesContentValid(studentExperienceFormState)
    ) {
      setHighlightMissingAdditionalContent(false);
    }
  }, [studentExperienceFormState, highlightMissingAdditionalContent]);

  /**
   * True after the first successful config fetch has been applied to form state.
   * Subsequent refetches (e.g. after save) only update savedState, not formState,
   * so a background refetch can never overwrite the user's unsaved edits.
   */
  const hasHydrated = useRef(false);

  /**
   * Incremented by handleSave right before updating the query cache.
   * While > 0 the hydration effect skips re-deriving savedState from the cache,
   * preventing a race where the cache-triggered effect overwrites the savedState
   * that handleSave just set (e.g. schoolSelection getting lost).
   * Decremented at the end of each skipped hydration run so that subsequent
   * genuine fetches still hydrate correctly.
   */
  const skipCacheHydrationCount = useRef(0);

  useEffect(() => {
    if (!config) {
      return;
    }

    // After a save, handleSave already set formState and savedState to the
    // exact normalised values that were persisted.  The cache updates from
    // the mutation onSuccess handlers trigger this effect again, but we must
    // not re-derive savedState from the cache — doing so can create object-
    // reference mismatches (e.g. a new links array) or lose fields that are
    // only in the payload but not echoed back in the response.
    if (skipCacheHydrationCount.current > 0) {
      skipCacheHydrationCount.current -= 1;
      return;
    }

    // On the first load populate both formState and savedState from the server.
    // On subsequent fetches (e.g. after save) only update savedState so that any
    // unsaved edits the user has made are never silently overwritten.
    const isInitialHydration = !hasHydrated.current;

    // Hydrate approvals
    const approvalUpdates: Partial<ApprovalsFormState> = {};
    if (
      config.approvalRequirement &&
      VALID_APPROVAL_REQUIREMENTS.has(config.approvalRequirement as string)
    ) {
      approvalUpdates.approvalRequirement = config.approvalRequirement as string;
    }
    if (
      config.planApprovalOption &&
      VALID_STUDENT_SUBMISSIONS.has(config.planApprovalOption as string)
    ) {
      approvalUpdates.studentSubmission = config.planApprovalOption as string;
    }
    if (
      config.staffApprovalOption &&
      VALID_STAFF_APPROVALS.has(config.staffApprovalOption as string)
    ) {
      approvalUpdates.staffApproval = config.staffApprovalOption as string;
    }
    if (Object.keys(approvalUpdates).length > 0) {
      if (isInitialHydration) {
        setApprovalsFormState((prev) => ({ ...prev, ...approvalUpdates }));
      }
      setApprovalsSavedState((prev) => ({ ...prev, ...approvalUpdates }));
    }

    // Hydrate credit recovery
    if (
      config.planFeatureOption === CREDIT_RECOVERY_VALUES.Prompt ||
      config.planFeatureOption === CREDIT_RECOVERY_VALUES.DoNotPrompt
    ) {
      if (isInitialHydration) {
        setCreditRecovery(config.planFeatureOption);
      }
      setSavedCreditRecovery(config.planFeatureOption);
    }

    // Hydrate grade settings & deficiency
    if (Array.isArray(config.gradeSettings)) {
      if (isInitialHydration) {
        setGradeSettings(config.gradeSettings);
      }
      setSavedGradeSettings(config.gradeSettings);
    }
    if (Array.isArray(config.gradeDeficiency)) {
      if (isInitialHydration) {
        setGradeDeficiency(config.gradeDeficiency);
      }
      setSavedGradeDeficiency(config.gradeDeficiency);
    }

    // Hydrate student experience
    const seUpdates: Partial<StudentExperienceFormState> = {};
    if (config.labels) {
      if (typeof config.labels.cluster === 'string') {
        seUpdates.clusterLabel = mapLegacyLabelToCurrentDefault(
          config.labels.cluster,
          LEGACY_CONFIGURE_LABELS_CLUSTER_DEFAULT,
          CONFIGURE_LABELS_CLUSTER_DEFAULT
        );
      }
      if (typeof config.labels.pathway === 'string') {
        seUpdates.pathwayLabel = mapLegacyLabelToCurrentDefault(
          config.labels.pathway,
          LEGACY_CONFIGURE_LABELS_PATHWAY_DEFAULT,
          CONFIGURE_LABELS_PATHWAY_DEFAULT
        );
      }
    }
    if (typeof config.termSetting === 'boolean') {
      seUpdates.enableTermSetting = config.termSetting;
    }
    if (Array.isArray(config.terms)) {
      seUpdates.terms = config.terms as string[];
    }
    if (typeof config.schoolSelection === 'string') {
      seUpdates.schoolSelection = config.schoolSelection;
    } else {
      // No schoolSelection in API response — default to AllStudents.
      seUpdates.schoolSelection = SCHOOL_SELECTION_VALUES.AllStudents;
    }
    if (Array.isArray(config.schoolSelectionGrades)) {
      seUpdates.schoolSelectionGrades = [...config.schoolSelectionGrades]
        .filter((g): g is number => typeof g === 'number' && Number.isInteger(g))
        .filter((g) => g >= 6 && g <= 12)
        .sort((a, b) => a - b);
    }
    if (config.schoolClusterGuide) {
      const g = config.schoolClusterGuide;
      seUpdates.showToStudents = g.isAvailable === true || g.isAvailable === 'true';
      seUpdates.documentName = typeof g.description === 'string' ? g.description : '';
      seUpdates.documentFileName = typeof g.name === 'string' ? g.name : '';
      seUpdates.links = Array.isArray(g.links)
        ? g.links.map((l) => ({ name: l.name ?? '', url: l.url ?? '' }))
        : [];
    }
    if (Object.keys(seUpdates).length > 0) {
      if (isInitialHydration) {
        setStudentExperienceFormState((prev) => ({ ...prev, ...seUpdates }));
      }
      setStudentExperienceSavedState((prev) => ({ ...prev, ...seUpdates }));
    }

    hasHydrated.current = true;
  }, [config]);

  const isApprovalsDirty = useMemo(
    () => isApprovalsFormDirty(approvalsFormState, approvalsSavedState),
    [approvalsFormState, approvalsSavedState]
  );

  const isAcademicDirty = useMemo(
    () =>
      creditRecovery !== savedCreditRecovery ||
      !arraysEqual(gradeSettings, savedGradeSettings) ||
      !arraysEqual(gradeDeficiency, savedGradeDeficiency),
    [
      creditRecovery,
      savedCreditRecovery,
      gradeSettings,
      savedGradeSettings,
      gradeDeficiency,
      savedGradeDeficiency,
    ]
  );

  const isStudentExperienceDirty = useMemo(
    () =>
      studentExperienceFormState.clusterLabel !== studentExperienceSavedState.clusterLabel ||
      studentExperienceFormState.pathwayLabel !== studentExperienceSavedState.pathwayLabel ||
      studentExperienceFormState.enableTermSetting !==
        studentExperienceSavedState.enableTermSetting ||
      studentExperienceFormState.schoolSelection !== studentExperienceSavedState.schoolSelection ||
      !arraysEqual(
        studentExperienceFormState.schoolSelectionGrades,
        studentExperienceSavedState.schoolSelectionGrades
      ) ||
      studentExperienceFormState.showToStudents !== studentExperienceSavedState.showToStudents ||
      studentExperienceFormState.documentName !== studentExperienceSavedState.documentName ||
      studentExperienceFormState.documentFileName !==
        studentExperienceSavedState.documentFileName ||
      !arraysEqual(studentExperienceFormState.terms, studentExperienceSavedState.terms) ||
      !arraysEqual(
        studentExperienceFormState.links,
        studentExperienceSavedState.links,
        (l: AdditionalResourcesLink) => `${l.name}|${l.url}`
      ),
    [studentExperienceFormState, studentExperienceSavedState]
  );

  const isAnyDirty = isApprovalsDirty || isAcademicDirty || isStudentExperienceDirty;

  /**
   * When the user confirms "leave without saving" and we call goToHome(), useBlocker
   * would re-fire for that programmatic navigate(). This ref lets us skip the block
   * for that one intentional navigation.
   */
  const allowNavigation = useRef(false);

  /**
   * URL captured from breadcrumb/link interception so handleLeaveWithoutSaving knows
   * where to go after confirmation.
   */
  const pendingNavUrl = useRef<string | null>(null);

  /**
   * True when the leave modal was opened by a back-button press (sentinel pop).
   * Tells handleLeaveWithoutSaving to call window.history.go(-1) to complete the
   * navigation rather than calling goToHome().
   */
  const isPendingBackNav = useRef(false);

  /**
   * Tracks whether the sentinel history entry has been pushed for the current
   * dirty session. Using a ref rather than state so it survives React 18
   * StrictMode's double-invocation of effects without pushing a duplicate entry.
   */
  const sentinelPushed = useRef(false);

  /**
   * Block PUSH / REPLACE navigations (React Router <Link>, navigate()) when dirty.
   *
   * Back-button (POP) blocking uses the sentinel approach below because
   * useBlocker silently skips POP when delta == null — which happens whenever
   * the user arrives on this page via a direct URL, a hard refresh, or any
   * navigation that did not go through the React Router data router.
   */
  const blocker = useBlocker(
    useCallback(
      ({ historyAction }: { historyAction: string }) =>
        historyAction !== 'POP' && !allowNavigation.current && isAnyDirty,
      [isAnyDirty]
    )
  );

  /**
   * On mount, remove any leftover sentinel entry from a previous visit
   * (e.g. user navigated away via a link while dirty — the sentinel stays in
   * the forward history until the component remounts).
   */
  useEffect(() => {
    if (window.history.state?._backGuard) {
      window.history.replaceState({}, '');
    }
  }, []);

  /**
   * Push a same-URL sentinel entry whenever the page becomes dirty, and
   * neutralise it (via replaceState, not go(-1)) when it becomes clean again.
   *
   * When the user presses back, the browser pops from the sentinel to the
   * real /configurations entry.  Because the URL does not change, React Router
   * treats the POP as a no-op and the component stays mounted.  The popstate
   * listener below then shows the leave-guard modal.
   */
  useEffect(() => {
    if (isAnyDirty) {
      if (!sentinelPushed.current) {
        sentinelPushed.current = true;
        window.history.pushState({ _backGuard: true }, '');
      }
    } else {
      if (sentinelPushed.current) {
        sentinelPushed.current = false;
        if (window.history.state?._backGuard) {
          window.history.replaceState({}, '');
        }
      }
    }
  }, [isAnyDirty]);

  /**
   * Detect when the sentinel is consumed (user pressed back).
   * The URL is unchanged so React Router did nothing; we show the modal here.
   */
  useEffect(() => {
    if (!isAnyDirty) {
      return;
    }

    const handlePopState = () => {
      if (allowNavigation.current) {
        return;
      }
      if (!window.history.state?._backGuard && !isPendingBackNav.current) {
        sentinelPushed.current = false;
        isPendingBackNav.current = true;
        setShowLeaveModal(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isAnyDirty]);

  /**
   * Intercept clicks on <a> tags anywhere in the document (including the host
   * shell's top Naviance nav, gear icon, help links) that would navigate away
   * from the current page. When the form is dirty we prevent the default
   * navigation, capture the target href, and show our custom Neon leave-guard
   * modal instead of the browser's native "Leave site?" dialog.
   *
   * We use capture phase so we see the event before any shadow-DOM handler
   * swallows it, and we check that the link actually changes the page
   * (different pathname/origin) to avoid blocking same-page anchors or React
   * Router links that are already handled by useBlocker.
   */
  useEffect(() => {
    if (!isAnyDirty) {
      return;
    }
    const handleDocumentClick = (e: MouseEvent) => {
      if (allowNavigation.current) {
        return;
      }
      // Walk up the DOM from the click target to find an <a> element.
      const anchor = (e.target as Element)?.closest?.('a');
      if (!anchor) {
        return;
      }
      const href = anchor.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('javascript:') ||
        href.startsWith('data:') ||
        href.startsWith('vbscript:')
      ) {
        return;
      }
      // Resolve the href to an absolute URL to compare against current location.
      let resolvedUrl: URL;
      try {
        resolvedUrl = new URL(href, window.location.href);
      } catch {
        return;
      }
      // Only intercept navigations that leave the current page
      // (different pathname or origin). Same-page hash changes are fine.
      const isSamePage =
        resolvedUrl.origin === window.location.origin &&
        resolvedUrl.pathname === window.location.pathname;
      if (isSamePage) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      pendingNavUrl.current = resolvedUrl.href;
      setShowLeaveModal(true);
    };
    document.addEventListener('click', handleDocumentClick, { capture: true });
    return () => document.removeEventListener('click', handleDocumentClick, { capture: true });
  }, [isAnyDirty]);

  const isModalOpen = blocker.state === 'blocked' || showLeaveModal;

  const goToHome = useCallback(
    (url?: string) => {
      const destination = url ?? getHomeUrl();
      allowNavigation.current = true;
      if (destination.startsWith('/courses') || destination === '/staff') {
        navigate(destination);
      } else {
        window.location.href = destination;
      }
    },
    [navigate]
  );

  const handleCancel = useCallback(() => {
    if (isAnyDirty) {
      pendingNavUrl.current = null;
      setShowLeaveModal(true);
    } else {
      goToHome();
    }
  }, [isAnyDirty, goToHome]);

  const handleSave = useCallback(async () => {
    if (!isAnyDirty) {
      return;
    }
    if (termSettingsEnabled && !areStudentExperienceTermsValid(studentExperienceFormState)) {
      setHighlightEmptyTermNames(true);
      showToast('error', { heading: TERM_NAMES_REQUIRED_TOAST_HEADING });
      return;
    }
    if (
      studentExperienceFormState.showToStudents &&
      !areAdditionalResourcesLinksValid(studentExperienceFormState.links)
    ) {
      setHighlightIncompleteLinks(true);
      return;
    }
    if (schoolSelectionEnabled && !areSchoolSelectionGradesValid(studentExperienceFormState)) {
      setHighlightSchoolSelectionGrades(true);
      return;
    }
    if (!isAdditionalResourcesContentValid(studentExperienceFormState)) {
      setHighlightMissingAdditionalContent(true);
      return;
    }
    const payload = {
      labels: {
        cluster: studentExperienceFormState.clusterLabel,
        pathway: studentExperienceFormState.pathwayLabel,
      },
      termSetting: termSettingsEnabled ? studentExperienceFormState.enableTermSetting : false,
      terms:
        termSettingsEnabled && studentExperienceFormState.enableTermSetting
          ? studentExperienceFormState.terms.filter((t) => t.trim() !== '')
          : [],
      schoolSelection: schoolSelectionEnabled
        ? studentExperienceFormState.schoolSelection
        : SCHOOL_SELECTION_VALUES.AllStudents,
      schoolSelectionGrades:
        schoolSelectionEnabled &&
        studentExperienceFormState.schoolSelection === SCHOOL_SELECTION_VALUES.SpecificGrades
          ? [...studentExperienceFormState.schoolSelectionGrades]
              .filter((g) => g >= 6 && g <= 12)
              .sort((a, b) => a - b)
          : [],
      approvalRequirement: approvalsFormState.approvalRequirement,
      planApprovalOption: approvalsFormState.studentSubmission,
      staffApprovalOption: approvalsFormState.staffApproval,
      planFeatureOption: creditRecovery,
      gradeSettings,
      gradeDeficiency,
    };
    // Additional resources (schoolClusterGuide) must be saved via a separate endpoint.
    // The real backend's PUT /config ignores schoolClusterGuide entirely.
    //
    // IMPORTANT: The two saves (PUT /config then PUT /school-cluster-guide) run
    // sequentially because both read-modify-write the same DB row.  Running them
    // in parallel caused a race where PUT /config could overwrite the cluster-guide
    // changes with a stale snapshot.
    //
    // If a new file was selected, the second step uses the upload flow (POST with
    // FormData to succeed-api).  Otherwise a plain PUT with JSON metadata is sent.
    const validLinks = studentExperienceFormState.links.filter((l) => l.url.trim() !== '');
    const documentNameToSave =
      studentExperienceFormState.documentName.trim() !== ''
        ? studentExperienceFormState.documentName
        : ADDITIONAL_RESOURCES_DOC_NAME_PLACEHOLDER;
    const schoolClusterGuidePayload = {
      isAvailable: studentExperienceFormState.showToStudents,
      description: documentNameToSave,
      name: studentExperienceFormState.documentFileName,
      links: validLinks,
    };
    // Build the normalized state that reflects exactly what will be persisted.
    const normalizedStudentExperienceState: StudentExperienceFormState = {
      ...studentExperienceFormState,
      links: validLinks,
      documentName: documentNameToSave,
      documentFile: undefined,
      // schoolSelectionGrades is only persisted for SpecificGrades; clear it otherwise
      // so that formState matches savedState and the dirty flag resets correctly.
      schoolSelectionGrades:
        schoolSelectionEnabled &&
        studentExperienceFormState.schoolSelection === SCHOOL_SELECTION_VALUES.SpecificGrades
          ? [...studentExperienceFormState.schoolSelectionGrades]
              .filter((g) => g >= 6 && g <= 12)
              .sort((a, b) => a - b)
          : [],
    };
    try {
      // Prevent the hydration effect from re-deriving savedState from the
      // cache updates triggered by the mutation onSuccess handlers.
      // Two mutations update the query cache sequentially, so the effect
      // may fire up to two times — skip both.
      skipCacheHydrationCount.current = 2;
      // Save config first, THEN school-cluster-guide.  Both endpoints
      // read-modify-write the same DB row.  Running them in parallel caused
      // a race: PUT /config's deepMerge restores the OLD schoolClusterGuide
      // (from its snapshot), overwriting the description/links that
      // PUT /school-cluster-guide just persisted.  Sequential order
      // guarantees the cluster-guide write sees the latest row.
      await putConfigMutateRef.current(payload);
      if (studentExperienceFormState.documentFile) {
        await uploadDocumentMutateRef.current({
          file: studentExperienceFormState.documentFile,
          ...schoolClusterGuidePayload,
        });
      } else {
        await putSchoolClusterGuideMutateRef.current(schoolClusterGuidePayload);
      }
      setApprovalsSavedState(approvalsFormState);
      setSavedCreditRecovery(creditRecovery);
      setSavedGradeSettings(structuredClone(gradeSettings));
      setSavedGradeDeficiency(structuredClone(gradeDeficiency));
      setStudentExperienceFormState(normalizedStudentExperienceState);
      setStudentExperienceSavedState(normalizedStudentExperienceState);
      showToast('success', { heading: CONFIGURATIONS_SAVE_TOAST_HEADING });
    } catch {
      // Save failed — reset the skip counter so the hydration effect isn't
      // permanently blocked for future cache updates.
      skipCacheHydrationCount.current = 0;
      showToast('error', { heading: CONFIGURATIONS_SAVE_ERROR_HEADING });
    }
  }, [
    isAnyDirty,
    approvalsFormState,
    creditRecovery,
    gradeSettings,
    gradeDeficiency,
    studentExperienceFormState,
    showToast,
    schoolSelectionEnabled,
    termSettingsEnabled,
  ]);

  const handleLeaveWithoutSaving = useCallback(() => {
    setShowLeaveModal(false);
    if (blocker.state === 'blocked') {
      blocker.proceed();
    } else if (isPendingBackNav.current) {
      isPendingBackNav.current = false;
      allowNavigation.current = true;
      window.history.go(-1);
    } else {
      const url = pendingNavUrl.current;
      pendingNavUrl.current = null;
      goToHome(url ?? undefined);
    }
  }, [blocker, goToHome]);

  const handleContinueEditing = useCallback(() => {
    pendingNavUrl.current = null;
    setShowLeaveModal(false);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    if (isPendingBackNav.current) {
      isPendingBackNav.current = false;
      sentinelPushed.current = true;
      window.history.pushState({ _backGuard: true }, '');
    }
  }, [blocker]);

  const closeLeaveModal = useCallback(() => {
    pendingNavUrl.current = null;
    setShowLeaveModal(false);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    if (isPendingBackNav.current) {
      isPendingBackNav.current = false;
      sentinelPushed.current = true;
      window.history.pushState({ _backGuard: true }, '');
    }
  }, [blocker]);

  const tabs = useMemo<Tab[]>(
    () => [
      { id: CONFIGURATIONS_TAB_IDS.Approvals, text: 'Approvals' },
      { id: CONFIGURATIONS_TAB_IDS.AcademicRules, text: 'Academic rules' },
      { id: CONFIGURATIONS_TAB_IDS.StudentExperience, text: 'Student experience' },
    ],
    []
  );

  /**
   * Breadcrumbs are defined here (not as a static constant) so we can attach
   * onUserClick to the home crumb. NeonBreadcrumbs renders plain <a> tags inside
   * a shadow DOM — those are not React Router navigations, so useBlocker never fires
   * for them. Instead we call e.preventDefault() inside onUserClick when dirty and
   * show the leave-guard modal ourselves.
   */
  const breadcrumbs = useMemo(
    () => [
      {
        text: CONFIGURATIONS_TEXT.BreadcrumbHome,
        url: getHomeUrl(),
        onUserClick: (e: Event) => {
          if (isAnyDirty) {
            e.preventDefault();
            pendingNavUrl.current = getHomeUrl();
            setShowLeaveModal(true);
          }
        },
      },
      { text: CONFIGURATIONS_TEXT.Title, url: '#' },
    ],
    [isAnyDirty]
  );

  const { setFooterNode } = usePageFooter();

  // Keep stable refs to the latest callbacks so the footer useEffect never
  // needs them as dependencies. Without this, each render produces new
  // handleCancel / handleSave instances → the effect re-runs → setFooterNode
  // triggers a state update in PageFooterProvider → Configurations re-renders
  // → infinite loop ("Maximum update depth exceeded").
  const handleCancelRef = useRef(handleCancel);
  handleCancelRef.current = handleCancel;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    setFooterNode(
      <div style={configurationsActionBarStyle}>
        <NeonButton
          id={CONFIGURATIONS_ID.ConfigurationsCancel}
          dataType={NeonButtonTypes.Borderless}
          dataText={CONFIGURATIONS_BUTTON_CANCEL}
          onClick={() => handleCancelRef.current()}
          disabled={isConfigLoading || !!configError}
        />
        <NeonButton
          id={CONFIGURATIONS_ID.ConfigurationsSave}
          dataType={NeonButtonTypes.Primary}
          dataText={CONFIGURATIONS_BUTTON_SAVE}
          onClick={() => handleSaveRef.current()}
          disabled={
            isConfigLoading ||
            !!configError ||
            !isAnyDirty ||
            putConfig.isPending ||
            uploadDocument.isPending
          }
        />
      </div>
    );
    return () => setFooterNode(null);
  }, [
    setFooterNode,
    isConfigLoading,
    configError,
    isAnyDirty,
    putConfig.isPending,
    uploadDocument.isPending,
  ]);

  return (
    <div style={pageWrapperStyle}>
      <div style={pageHeaderStyle}>
        <NeonBreadcrumbs breadcrumbs={breadcrumbs} data-id={CONFIGURATIONS_ID.Breadcrumbs} />
        <h1 style={titleStyle}>{CONFIGURATIONS_TEXT.Title}</h1>
      </div>
      <div style={contentCardStyle}>
        <div style={{ flexShrink: 0, overflow: 'hidden' }}>
          <NeonTabs
            id={CONFIGURATIONS_ID.Tabs}
            tabs={tabs}
            currentTab={currentTab}
            dataLegend="Configurations sections"
            tabSelectionChanged={(tabId) =>
              setCurrentTab(tabId ?? CONFIGURATIONS_TAB_IDS.Approvals)
            }
          />
        </div>
        <div style={tabPanelStyle} role="tabpanel" aria-labelledby={`tab-${currentTab}`}>
          {configError && (
            <div style={configStatusContentStyle}>
              <NeonCardStandard>
                <div data-slot={DATA_SLOT} style={configStatusContentStyle}>
                  <NeonErrorPageContent dataErrorCode="500" />
                </div>
              </NeonCardStandard>
            </div>
          )}
          {!configError && isConfigLoading && (
            <div style={configStatusContentStyle}>
              <p style={paragraphStyle('14px', '20px')}>Loading configuration…</p>
            </div>
          )}
          {!configError && !isConfigLoading && (
            <>
              {currentTab === CONFIGURATIONS_TAB_IDS.Approvals && (
                <ApprovalsTab
                  key="approvals"
                  formState={approvalsFormState}
                  onFormChange={setApprovalsFormState}
                  showStudentSubmissionRules={shouldShowStudentSubmissionRules(
                    approvalsFormState.approvalRequirement,
                    planApprovalOptionsEnabled
                  )}
                  showStaffApprovalRules={
                    approvalsFormState.approvalRequirement !==
                    APPROVAL_REQUIREMENT_VALUES.NoApproval
                  }
                />
              )}
              {currentTab === CONFIGURATIONS_TAB_IDS.AcademicRules && (
                <AcademicRulesTab
                  key="academic-rules"
                  creditRecovery={creditRecovery}
                  onCreditRecoveryChange={setCreditRecovery}
                  gradeSettings={gradeSettings}
                  onGradeSettingsChange={setGradeSettings}
                  gradeDeficiency={gradeDeficiency}
                  onGradeDeficiencyChange={setGradeDeficiency}
                />
              )}
              {currentTab === CONFIGURATIONS_TAB_IDS.StudentExperience && (
                <StudentExperienceTab
                  key="student-experience"
                  formState={studentExperienceFormState}
                  onFormChange={setStudentExperienceFormState}
                  showTermSettings={termSettingsEnabled}
                  showSchoolSelection={schoolSelectionEnabled}
                  highlightEmptyTermNames={highlightEmptyTermNames}
                  highlightIncompleteLinks={highlightIncompleteLinks}
                  highlightSchoolSelectionGrades={highlightSchoolSelectionGrades}
                  highlightMissingAdditionalContent={highlightMissingAdditionalContent}
                />
              )}
            </>
          )}
        </div>
      </div>

      {isModalOpen && (
        <NeonModalDialog
          id={CONFIGURATIONS_ID.LeaveModal}
          dataSize="small"
          dataShowCloseButton
          neonDialogHasClosed={closeLeaveModal}
        >
          <div data-slot="dialog-header-title">{LEAVE_MODAL_TITLE}</div>
          <div data-slot="dialog-body">
            <div style={modalBodyTextStyle}>{LEAVE_MODAL_MESSAGE}</div>
          </div>
          <div data-slot="dialog-button-footer">
            <NeonButtonFooter
              id="leave-modal-button-footer"
              buttons={[
                {
                  id: 'leave-without-saving-btn',
                  text: LEAVE_MODAL_LEAVE,
                  type: NeonButtonTypes.Borderless,
                  onClick: (): void => handleLeaveWithoutSaving(),
                },
              ]}
              primaryButton={{
                id: 'continue-editing-btn',
                text: LEAVE_MODAL_CONTINUE,
                type: NeonButtonTypes.Primary,
                onClick: (): void => handleContinueEditing(),
              }}
            />
          </div>
        </NeonModalDialog>
      )}
    </div>
  );
}
